"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import Link from "next/link"
import { RefreshCw, AlertTriangle, ArrowRight } from "lucide-react"
import posthog from "posthog-js"
import { useLogger } from "@/lib/use-logger"
import { isWebpackError, getErrorTypeForAnalytics } from "@/utils/webpack-error-detector"
import type { ErrorBoundaryProps } from "@/types/error-boundary"
import DonistaLogo from "@/components/icons/donista-logo"

const strings = {
  de: {
    error: {
      title: "Etwas ist schiefgelaufen",
      message: "Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut.",
      reload: "Seite neu laden",
      retry: "Erneut versuchen",
      goHome: "Zur Startseite",
    },
    webpack: {
      title: "App wird aktualisiert",
      message: "Eine neue Version wurde bereitgestellt. Die Seite muss neu geladen werden.",
      autoReload: (s: number) => `Automatisches Neuladen in ${s} Sekunden`,
      reload: "Jetzt neu laden",
      retry: "Nochmal versuchen",
    },
  },
  en: {
    error: {
      title: "Something went wrong",
      message: "An unexpected error occurred. Please try again.",
      reload: "Reload Page",
      retry: "Try Again",
      goHome: "Go to Home",
    },
    webpack: {
      title: "App is updating",
      message: "A new version has been deployed. The page needs to be reloaded.",
      autoReload: (s: number) => `Auto-reloading in ${s} seconds`,
      reload: "Reload now",
      retry: "Try again",
    },
  },
} as const

function detectLocale(): "de" | "en" {
  if (typeof window === "undefined") return "de"
  const path = window.location.pathname
  return path.startsWith("/en/") || path === "/en" ? "en" : "de"
}

export default function Error({ error, reset }: ErrorBoundaryProps) {
  const log = useLogger(import.meta.url)
  const [locale] = useState(detectLocale)
  const [countdown, setCountdown] = useState(10)

  const isWebpackErrorDetected = isWebpackError(error)
  const t = strings[locale]
  const homePath = locale === "en" ? "/en" : "/"

  useEffect(() => {
    posthog.captureException(error, {
      $set: {
        error_type: getErrorTypeForAnalytics(error, "page"),
        error_location: "error_boundary",
      },
    })
    log.error(error)
  }, [log, error])

  useEffect(() => {
    if (!isWebpackErrorDetected) return undefined

    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => prev - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }

    window.location.reload()
    return undefined
  }, [isWebpackErrorDetected, countdown])

  const handleReload = () => {
    posthog.capture("error_page_hard_refresh", {
      error_type: isWebpackErrorDetected ? "webpack" : "other",
    })
    window.location.reload()
  }

  const label = isWebpackErrorDetected ? t.webpack : t.error

  return (
    <>
      <style>{`
        @keyframes errorFadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .error-fade-up {
          opacity: 0;
          animation: errorFadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
      <div
        className="fixed inset-0 flex flex-col items-center justify-center text-center px-4 z-50"
        style={{
          backgroundImage: "url('/background/ffflux.svg')",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      >
        <div className="error-fade-up">
          <DonistaLogo className="h-10 w-10 mx-auto mb-14 animate-[pulse_4s_ease-in-out_infinite]" />
        </div>

        <div
          className="mb-6 rounded-2xl bg-destructive/8 p-5 error-fade-up"
          style={{ animationDelay: "0.15s" }}
        >
          <AlertTriangle className="h-10 w-10 text-destructive" />
        </div>

        <h1
          className="text-3xl md:text-4xl font-bold mb-3 error-fade-up"
          style={{ animationDelay: "0.3s" }}
        >
          {label.title}
        </h1>
        <p
          className="text-lg text-muted-foreground max-w-md mb-8 error-fade-up"
          style={{ animationDelay: "0.4s" }}
        >
          {label.message}
        </p>

        {isWebpackErrorDetected && countdown > 0 && (
          <div
            className="w-full max-w-sm rounded-xl bg-background/80 backdrop-blur-sm p-5 mb-8 error-fade-up"
            style={{ animationDelay: "0.45s" }}
          >
            <p className="text-sm font-medium mb-3">
              {t.webpack.autoReload(countdown)}
            </p>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-1000"
                style={{ width: `${((10 - countdown) / 10) * 100}%` }}
              />
            </div>
          </div>
        )}

        <div
          className="flex flex-col sm:flex-row gap-3 error-fade-up"
          style={{ animationDelay: "0.5s" }}
        >
          <Button onClick={handleReload} size="lg" className="group px-8">
            <RefreshCw className="h-4 w-4 mr-2 group-hover:animate-spin" />
            {label.reload}
          </Button>
          <Button onClick={reset} variant="outline" size="lg" className="px-8">
            {label.retry}
          </Button>
        </div>

        {!isWebpackErrorDetected && (
          <Link
            href={homePath}
            className="mt-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors group error-fade-up"
            style={{ animationDelay: "0.6s" }}
          >
            {t.error.goHome}
            <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </Link>
        )}
      </div>
    </>
  )
}
